// src/pages/HotelList/HotelList.jsx
import React, { useMemo, useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Container, Row, Col, Button, Offcanvas, Alert } from "react-bootstrap";
import { HotelSearchBar, HotelFilters, HotelCard } from "../../Features/HotelList";
import { NotFound } from "../../components";
import { HotelDetails } from "../../Features/CustomPackages";
import Cookies from "js-cookie"
import "./HotelList.css";
import { InfoRounded } from "@mui/icons-material";

const DEFAULT_QUERY = {
  destination: "Dubai, United Arab Emirates",
  destinationId: "",
  checkin: "",
  checkout: "",
  travelers: "2",
  rooms: "1",
  q: "",
  minPrice: "0",
  maxPrice: "300",
  refundable: "false",
  amenities: "",
  minRating: "0",
  sort: "price_asc",
  page: "1",
  search_type: "city", 
  search_query: "",   
};


export default function HotelList({ url }) {

    
  const Lang = Cookies.get("preferred_language") || "en";

  const API_URL = `${url}/api/method/alltimes.www.hotel_api.search`;

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate(); 
  const [showFilter, setShowFilter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hotels, setHotels] = useState([]);
  const [error, setError] = useState(null);

  const [rateLimit, setRateLimit] = useState(null);
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedHotel, setSelectedHotel] = useState(null); 


  const query = useMemo(() => {
  const obj = { ...DEFAULT_QUERY };
  for (const k of Object.keys(obj)) {
    const v = searchParams.get(k);
    if (v !== null) obj[k] = v;
  }

  // 👇 Parse travelers JSON إذا موجود
  const travelersParam = searchParams.get("travelers");
  if (travelersParam) {
    try {
      obj.travelers_data = JSON.parse(travelersParam);
    } catch (e) {
      console.error("Invalid travelers JSON:", e);
      obj.travelers_data = [
        { Adults: 2, Children: 0, ChildrenAges: [] }
      ];
    }
  }

  return obj;
}, [searchParams]);

  const [travelers, setTravelers] = useState(
    query.travelers_data || [
      {
        Adults: 2,
        Children: 0,
        ChildrenAges: [],
      },
    ]
  );


  useEffect(() => {
    if (query.travelers_data && isInitialLoad) {
      setTravelers(query.travelers_data);
      setIsInitialLoad(false);
    }
  }, [query.travelers_data]);


  const [searchBarState, setSearchBarState] = useState({
    destinationId: query.name,
    destination: query.destination,
    checkin: query.checkin,
    checkout: query.checkout,
    travelers: [{ adults: 2, children: [] }],
    rooms: query.rooms,
    search_type: query.search_type,
    search_query: query.search_query
  });
  





  const updateSearchParams = (updates, triggerImmediately = false) => {
    const next = { 
      ...query, 
      ...updates, 
      page: updates.page ?? "1",
      search_query: updates.search_query ?? query.search_query,
      search_type: updates.search_type ?? query.search_type
    };
    setSearchParams(next, { replace: false });
    
    if (triggerImmediately) {
      setSearchTrigger(prev => prev + 1);
    }
  };

  // Update function for search bar (now triggers fetch immediately)
  const updateSearchBar = (updates) => {
    setSearchBarState(prev => ({ ...prev, ...updates }));
  };

  // Update function for filters (triggers fetch immediately)
  const updateFilters = (updates) => {
    updateSearchParams(updates, true);
  };

  const onDiscover = () => {
    const updates = {
      ...searchBarState,
      q: String(Date.now()),
      travelers_data: JSON.stringify(travelers), // Use current state, not from URL
      refundable: query.refundable,
      minRating: query.minRating,
    };

    setSearchParams(updates, { replace: false });
    setSearchTrigger(prev => prev + 1);
    setIsInitialLoad(false); // Prevent future sync from URL
  };

  const handleTravelersChange = (newTravelers) => {
  setTravelers(newTravelers);
  setIsInitialLoad(false); // Mark as edited to prevent URL sync
};

  const buildPayload = () => ({
    check_in: searchBarState.checkin || "2025-11-12", 
    check_out: searchBarState.checkout || "2025-11-15", 
    rating: parseInt(query.minRating) || 0,
    nationality: "AE",
    refundable: query.refundable === "true",
    rooms: travelers, // Keep using the travelers state from component
    search_type: searchBarState.search_type || "city",
    search_query: searchBarState.search_query || ""
  });

  // Update the initial searchBarState to use query values
useEffect(() => {
 
  setSearchBarState({
   destinationId: query.destinationId || "", 
    destination: query.destination || DEFAULT_QUERY.destination,
    checkin: query.checkin || DEFAULT_QUERY.checkin,
    checkout: query.checkout || DEFAULT_QUERY.checkout,
    travelers: query.travelers || DEFAULT_QUERY.travelers,
    rooms: query.rooms || DEFAULT_QUERY.rooms,
    search_type: query.search_type || DEFAULT_QUERY.search_type,
    search_query: query.search_query || DEFAULT_QUERY.search_query
  });
}, [query.destination, query.checkin, query.checkout, query.travelers, 
    query.rooms, query.search_type, query.search_query]);

  useEffect(() => {
    if (isInitialLoad && !query.search_query) {
      setIsInitialLoad(false);
      return;
    }

    const controller = new AbortController();
    const payload = buildPayload();

    (async () => {
      try {
        setLoading(true);
        setError(null);
        setErrorMessage("");
        setHotels([]);

        const res = await fetch(API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });
  
        const rl = {
          reset: res.headers.get("X-RateLimit-Reset"),
          limit: res.headers.get("X-RateLimit-Limit"),
          remaining: res.headers.get("X-RateLimit-Remaining"),
          used: res.headers.get("X-RateLimit-Used"),
        };
             
        setRateLimit(rl);

        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || `Request failed with ${res.status}`);
        }
   
        const data = await res.json();
        if (data.status_code === 201) {
          setErrorMessage(data.message);
          setHotels([]);
        }

        const list = Array.isArray(data?.result) ? data.result : [];
        const mapped = list.map(transformHotelFromAPI);
        setHotels(mapped);
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(err.message || "Request failed");
          setHotels([]);
        }
      }  finally {
        setLoading(false);
 
      }
    })();

    return () => controller.abort();
  }, [ query]);


  const handleHotelClick = (hotel) => {
    // Navigate to hotel details page with hotel data in state
    navigate(`/hotels/${hotel.id}`, {
      state: {
        hotelData: {
          ...hotel,
          selectedDestination: searchBarState.destinationId,
          travelers: formatTravelersData(travelers)
        },
        checkin: searchBarState.checkin,
        checkout: searchBarState.checkout
      }
    });
  };
  

  const handleBack = () => {
    setSelectedHotel(null);
  };

  return (
    <div className="py-3">
      {selectedHotel ? ( 
        <Container>
          <HotelDetails
            dataFromList={selectedHotel}
            onReturn={handleBack}
            checkin={searchBarState.checkin}
            checkout={searchBarState.checkout}
            url={url}
          />
        </Container>
        
      ) : (
        <>
           <Container>
            <div className="d-xl-none hotellist-filter-button-container">
              <span>Hotel Search Results</span>
              <Button
                variant="outline-primary"
                onClick={() => setShowFilter(true)}
                className="d-flex align-items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path
                    d="M16.7083 9.00001H6.41251M2.77834 9.00001H1.29167M2.77834 9.00001C2.77834 8.5182 2.96974 8.05612 3.31043 7.71543C3.65112 7.37474 4.1132 7.18334 4.59501 7.18334C5.07682 7.18334 5.53889 7.37474 5.87958 7.71543C6.22027 8.05612 6.41167 8.5182 6.41167 9.00001C6.41167 9.48182 6.22027 9.94389 5.87958 10.2846C5.53889 10.6253 5.07682 10.8167 4.59501 10.8167C4.1132 10.8167 3.65112 10.6253 3.31043 10.2846C2.96974 9.94389 2.77834 9.48182 2.77834 9.00001ZM16.7083 14.5058H11.9183M11.9183 14.5058C11.9183 14.9878 11.7265 15.4504 11.3857 15.7911C11.0449 16.1319 10.5828 16.3233 10.1008 16.3233C9.61903 16.3233 9.15695 16.1311 8.81626 15.7904C8.47557 15.4497 8.28417 14.9876 8.28417 14.5058M11.9183 14.5058C11.9183 14.0239 11.7265 13.5622 11.3857 13.2214C11.0449 12.8806 10.5828 12.6892 10.1008 12.6892C9.61903 12.6892 9.15695 12.8806 8.81626 13.2213C8.47557 13.562 8.28417 14.024 8.28417 14.5058M8.28417 14.5058H1.29167M16.7083 3.49417H14.1208M10.4867 3.49417H1.29167M10.4867 3.49417C10.4867 3.01236 10.6781 2.55029 11.0188 2.20959C11.3595 1.8689 11.8215 1.67751 12.3033 1.67751C12.5419 1.67751 12.7781 1.7245 12.9985 1.81579C13.219 1.90709 13.4192 2.0409 13.5879 2.20959C13.7566 2.37829 13.8904 2.57856 13.9817 2.79896C14.073 3.01937 14.12 3.2556 14.12 3.49417C14.12 3.73274 14.073 3.96897 13.9817 4.18938C13.8904 4.40979 13.7566 4.61006 13.5879 4.77875C13.4192 4.94744 13.219 5.08126 12.9985 5.17255C12.7781 5.26385 12.5419 5.31084 12.3033 5.31084C11.8215 5.31084 11.3595 5.11944 11.0188 4.77875C10.6781 4.43806 10.4867 3.97598 10.4867 3.49417Z"
                    stroke="#23459B" strokeWidth="1.5" strokeLinecap="round" strokeMiterlimit="10"
                  />
                </svg>
              </Button>
            </div>

            <Row className="g-4">
              <Col className="hotellist-HotelFilters-container d-none d-xl-block" xl={3}>
                <HotelFilters 
                  value={query} 
                  onChange={updateFilters} 
                />
              </Col>

              <Col xs={12} xl={9}>
                <div className="hotel-searchbar-fixed">
                  <Container>
                    <Row className="g-4 align-items-center">
                      <Col xs={12} xl={9} className={`${Lang === "en" ? "ms-auto" : "me-auto"}`}>
                        <div className="hotel-searchbar-inner">
                          <HotelSearchBar 
                            value={searchBarState} 
                            onChange={setSearchBarState} // Pass the setter function
                            onDiscover={onDiscover}
                            travelers={travelers}
                            setTravelers={setTravelers}
                            onTravelersChange={handleTravelersChange}
                          />
                        </div>
                      </Col>
                    </Row>
                  </Container>
                </div>

                <div className="hotel-searchbar-spacer" />

                {/* {error && (
                  <Alert variant="danger" className="mb-3">
                    {error}
                    {rateLimit?.remaining !== null && (
                      <div className="small mt-2 text-muted">
                        Rate limit: {rateLimit.remaining ?? "-"} / {rateLimit.limit ?? "-"} (reset in {rateLimit.reset ?? "-"}s)
                      </div>
                    )}
                  </Alert>
                )} */}

                {loading &&
                  Array.from({ length: 6 }).map((_, i) => (
                    <div key={`sk-${i}`} className="mb-3">
                      <HotelCard loading  />
                    </div>
                  ))
                }

                {!loading &&
                  hotels.map((h) => (
                    <div key={`${h.id}-${h.name}`} className="mb-3">
                      <HotelCard
                        hotel={h} 
                        onClick={() => handleHotelClick(h)}
                      />
                    </div>
                  ))
                }

              {!loading && error ? (
                  <NotFound />
                ) : errorMessage ? (
                  <NotFound
                    errorCode={errorMessage ? false : true}
                    errorMessage={errorMessage ? errorMessage : ""}
                    description={errorMessage ? "Try adjusting your filters or dates" : ""}
                  />
                ) : null}
            
              </Col>
            </Row>
          </Container>

          <Offcanvas
            show={showFilter}
            onHide={() => setShowFilter(false)}
            placement="start"
            className="hotellist-offcanvas"
          >
            <Offcanvas.Header closeButton className="hotellist-offcanvas-header">
              <Offcanvas.Title>Filters</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="hotellist-offcanvas-body">
              <HotelFilters value={query} onChange={updateFilters} />
            </Offcanvas.Body>
          </Offcanvas>
        </>
      )
    }
    </div>
  );
}

function transformHotelFromAPI(item) {
  const info = item?.HotelInfo ?? {};
  const room0 = Array.isArray(item?.Rooms) && item.Rooms.length ? item.Rooms[0] : null;

  let price = null;
  const day0 = room0?.DayRates?.[0]?.[0]?.BasePrice;
  if (typeof day0 === "number") price = Math.round(day0);
  if (price === null && typeof room0?.TotalFare === "number") {
    price = Math.round(room0.TotalFare);
  }

  const features = Array.isArray(info.HotelFacilities)
    ? info.HotelFacilities
    : [];

  const dis = stripHtml(info.Description || "").slice(0, 300);

  return {
    id: info.HotelCode || item.HotelCode,
    name: info.HotelName || "Hotel",
    city: info.CityName || info.CountryName || "",
    area: (info.Address || "").split(",")?.[1]?.trim() || "",
    image: info.Image,
    Images: info.Images,
    features,
    promo: room0?.RoomPromotion?.[0] ?? null,
    dis,
    price: price ?? 0,
    oldPrice: null,
    rating: info.HotelRating ?? 0,
    refundable: !!room0?.IsRefundable,
    address: info.Address,
    Map: info.Map,
    rooms: item.Rooms 
  };
}

function stripHtml(html) {
  if (!html) return "";
  return String(html).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}


function formatTravelersData(travelersArray) {
  let adults = 0;
  let children = 0;
  let children_ages = [];

  travelersArray.forEach(room => {
    adults += room.Adults;
    children += room.Children;
    children_ages = children_ages.concat(room.ChildrenAges || []);
  });

  return {
    adults,
    children,
    children_ages
  };
}